import axios, { AxiosInstance, AxiosRequestHeaders, AxiosResponse } from 'axios';
import { API_BASE_URL } from './apiConstant';
import { getToken } from './auth';
 
const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 600000,
});
 
const getAuthHeaders = (useAuth: boolean, isMultipart: boolean = false) => {
  const headers: Record<string, string> = {};
 
  // Add content-type dynamically
  headers['Content-Type'] = isMultipart
    ? 'multipart/form-data'
    : 'application/json';
 
  if (useAuth) {
    const token = getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    } else {
      throw new Error('No token found in localStorage');
    }
  }
  return headers;
};
 
/**
 * GET with optional Authorization header.
 */
const getWithOptionalAuthorization = async (
  endpoint: string,
  useAuth: boolean = false
): Promise<AxiosResponse> => {
  const headers = getAuthHeaders(useAuth);
  return api.get(endpoint, { headers });
};
 
/**
 * POST with optional Authorization header.
 */
type PostBody = Record<string, any> | FormData;
 
const postWithOptionalAuthorization = async (
  endpoint: string,
  body: PostBody = {},
  useAuth: boolean = false,
  isMultipart: boolean = false,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse> => {
  const headers = getAuthHeaders(useAuth, isMultipart);
  return api.post(endpoint, body, 
    { 
      headers,
    ...config,
    }
  );
};
 
const getWithOptionalAuthorizationGeneric = async <T>(
  endpoint: string,
  useAuth: boolean = false
): Promise<T> => {
  const headers = getAuthHeaders(useAuth);
  const response: AxiosResponse<T> = await api.get(endpoint, { headers });
  return response;
};
 
const putWithOptionalAuthorization = async (
  endpoint: string,
  body: PostBody = {},
  useAuth: boolean = false,
  isMultipart: boolean = false,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse> => {
  const headers = getAuthHeaders(useAuth, isMultipart);
  return api.put(endpoint, body, { headers, ...config });
};
 
const deleteWithOptionalAuthorization = async (
  endpoint: string,
  useAuth: boolean = false,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse> => {
  const headers = getAuthHeaders(useAuth);
  return api.delete(endpoint, { headers, ...config });
};

const patchWithOptionalAuthorization = async (
  endpoint: string,
  body: PostBody = {},
  useAuth: boolean = false,
  isMultipart: boolean = false,
  config: AxiosRequestConfig = {}
): Promise<AxiosResponse> => {
  const headers = getAuthHeaders(useAuth, isMultipart);
  return api.patch(endpoint, body, { headers, ...config });
};
 
export {
  api,
  getWithOptionalAuthorization,
  postWithOptionalAuthorization,
  getWithOptionalAuthorizationGeneric,
  putWithOptionalAuthorization,
  deleteWithOptionalAuthorization,
  patchWithOptionalAuthorization
};